import React from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface GallerySliderModalProps {
    isOpen: boolean;
    onClose: () => void;
    images: string[];
    initialIndex: number;
    getImageUrl: (url: string) => string;
}

const CustomNextArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button
            onClick={onClick}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[10002] w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md border border-white/20 transition-all group"
        >
            <i className="fa-solid fa-chevron-right text-xl md:text-2xl transition-transform group-hover:scale-110"></i>
        </button>
    );
};

const CustomPrevArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button
            onClick={onClick}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[10002] w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md border border-white/20 transition-all group"
        >
            <i className="fa-solid fa-chevron-left text-xl md:text-2xl transition-transform group-hover:scale-110"></i>
        </button>
    );
};

const GallerySliderModal: React.FC<GallerySliderModalProps> = ({ 
    isOpen, 
    onClose, 
    images, 
    initialIndex, 
    getImageUrl 
}) => {
    const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
    const sliderRef = React.useRef<Slider | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            // Small timeout to ensure slider is mounted before telling it where to go
            setTimeout(() => {
                sliderRef.current?.slickGoTo(initialIndex, true);
            }, 50);

            // Handle Escape key
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose();
            };
            window.addEventListener('keydown', handleEscape);
            return () => window.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, initialIndex, onClose]);

    if (!isOpen) return null;

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: initialIndex,
        beforeChange: (oldIndex: number, newIndex: number) => setCurrentIndex(newIndex),
        nextArrow: <CustomNextArrow />,
        prevArrow: <CustomPrevArrow />,
        fade: true
    };

    return (
        <div 
            className="fixed inset-0 z-[9999] flex flex-col bg-black/95 backdrop-blur-xl animate__animated animate__fadeIn"
            onClick={onClose}
        >
            {/* Top Toolbar */}
            <div 
                className="absolute top-0 inset-x-0 p-6 flex justify-between items-center z-[10005]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-4">
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-2 rounded-full text-white font-bold text-sm">
                        {currentIndex + 1} <span className="opacity-50 mx-1">/</span> {images.length}
                    </div>
                </div>
                
                <button 
                    onClick={onClose}
                    className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-red-500/80 text-white rounded-full transition-all border border-white/20 active:scale-90"
                >
                    <i className="fa-solid fa-xmark text-xl"></i>
                </button>
            </div>

            {/* Main Slider Container */}
            <div 
                className="flex-1 w-full flex items-center justify-center p-4 md:p-12"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="w-full h-full max-w-7xl mx-auto flex items-center justify-center relative">
                    <Slider ref={sliderRef} {...settings} className="w-full h-full gallery-main-slider">
                        {images.map((img, i) => (
                            <div key={i} className="outline-none h-full w-full flex items-center justify-center">
                                <div className="h-[80vh] flex items-center justify-center">
                                    <img 
                                        src={getImageUrl(img)} 
                                        alt={`Project View ${i + 1}`} 
                                        className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl animate__animated animate__zoomIn animate__faster"
                                    />
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>

            {/* Bottom Thumbnails Strip (Optional but nice) */}
            <div 
                className="p-8 hidden md:flex justify-center gap-3 overflow-x-auto no-scrollbar z-[10005]"
                onClick={(e) => e.stopPropagation()}
            >
                {images.map((img, i) => (
                    <div 
                        key={i}
                        onClick={() => sliderRef.current?.slickGoTo(i)}
                        className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${currentIndex === i ? 'border-accent scale-110 shadow-lg' : 'border-white/20 opacity-50 hover:opacity-100'}`}
                    >
                        <img src={getImageUrl(img)} className="w-full h-full object-cover" alt="" />
                    </div>
                ))}
            </div>

            <style>
                {`
                .gallery-main-slider .slick-list,
                .gallery-main-slider .slick-track,
                .gallery-main-slider .slick-slide > div {
                    height: 100%;
                    outline: none;
                }
                .gallery-main-slider .slick-slide {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                `}
            </style>
        </div>
    );
};

export default GallerySliderModal;
